import React from 'react';
import { motion } from 'framer-motion';
import { Microscope, Calculator, BookText, Palette, Briefcase, Globe } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const CollegeAcademics = () => {
  const departments = [
    {
      icon: Microscope,
      name: 'Science',
      subjects: ['Physics', 'Chemistry', 'Biology', 'Further Mathematics', 'Agricultural Science'],
      color: 'blue',
    },
    {
      icon: BookText,
      name: 'Arts',
      subjects: ['Literature in English', 'Government', 'History', 'Christian Religious Studies', 'Yoruba'],
      color: 'purple',
    },
    {
      icon: Briefcase,
      name: 'Commercial',
      subjects: ['Accounting', 'Economics', 'Commerce', 'Business Studies', 'Office Practice'],
      color: 'green',
    },
  ];

  const coreSubjects = [
    'English Language',
    'Mathematics',
    'Civic Education',
    'Computer Studies',
    'Physical Education',
  ];

  return (
    <>
      <PageHeader
        title="Academics"
        subtitle="Comprehensive education for academic excellence"
        breadcrumbs={[
          { label: 'Our Schools', href: '/our-schools' },
          { label: 'College', href: '/college' },
          { label: 'Academics' },
        ]}
      />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
              Academic Departments
            </h2>
            <p className="text-lg text-gray-600">
              Students choose their path based on their interests and career aspirations,
              with guidance from our academic counselors.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {departments.map((dept, index) => {
              const Icon = dept.icon;
              return (
                <motion.div
                  key={dept.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8"
                >
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="text-primary-600" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{dept.name} Department</h3>
                  <ul className="space-y-2">
                    {dept.subjects.map((subject) => (
                      <li key={subject} className="flex items-center gap-2 text-gray-600">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                        {subject}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary-50 rounded-2xl p-8"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Core Subjects (All Students)</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {coreSubjects.map((subject) => (
                <span key={subject} className="px-4 py-2 bg-white rounded-full text-gray-700 shadow-soft">
                  {subject}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Examination Preparation
              </h2>
              <p className="text-gray-600 mb-6">
                We prepare students thoroughly for national examinations including:
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-soft">
                  <h4 className="font-semibold text-gray-900">WAEC (SSCE)</h4>
                  <p className="text-sm text-gray-600">West African Senior School Certificate Examination</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-soft">
                  <h4 className="font-semibold text-gray-900">NECO</h4>
                  <p className="text-sm text-gray-600">National Examination Council</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-soft">
                  <h4 className="font-semibold text-gray-900">JAMB (UTME)</h4>
                  <p className="text-sm text-gray-600">Joint Admissions and Matriculation Board</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=600&h=400&fit=crop"
                alt="Students studying"
                className="w-full h-[400px] object-cover rounded-2xl shadow-hard"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CollegeAcademics;
